import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import ConfirmDialog from '../ui/ConfirmDialog';
import {
  adminGetShopMembers,
  adminShopModerationAction,
  adminShopModerationActionData,
  type AdminModerationShopRow,
  type AdminShopMember,
} from '../../services/api/adminShopModeration';
import { useToast } from '../../contexts/ToastContext';

interface AdminShopMembersModalProps {
  shop: AdminModerationShopRow | null;
  isOpen: boolean;
  onClose: () => void;
  onChanged?: () => void;
}

type MemberRole = 'owner' | 'member';

/** Admin manage shop_members: add by username, change role, remove. */
export const AdminShopMembersModal: React.FC<AdminShopMembersModalProps> = ({
  shop,
  isOpen,
  onClose,
  onChanged,
}) => {
  const { showToast } = useToast();
  const [members, setMembers] = useState<AdminShopMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [username, setUsername] = useState('');
  const [newRole, setNewRole] = useState<MemberRole>('member');
  const [formError, setFormError] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<AdminShopMember | null>(null);

  const load = useCallback(async () => {
    if (!shop) return;
    setLoading(true);
    setLoadError(null);
    try {
      setMembers(await adminGetShopMembers(shop.id));
    } catch (err: unknown) {
      setLoadError(err instanceof Error ? err.message : 'Could not load members');
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [shop]);

  useEffect(() => {
    if (isOpen && shop) {
      setUsername('');
      setNewRole('member');
      setFormError(null);
      setRemoveTarget(null);
      void load();
    }
  }, [isOpen, shop, load]);

  if (!shop) return null;

  const ownerCount = members.filter((m) => m.role === 'owner').length;

  const addMember = async (e: React.FormEvent) => {
    e.preventDefault();
    const handle = username.trim().replace(/^@/, '');
    if (!handle) {
      setFormError('Enter a username or user id.');
      return;
    }
    setAdding(true);
    setFormError(null);
    try {
      const payload: Record<string, unknown> = {
        action: 'add_member',
        shop_id: shop.id,
        role: newRole,
      };
      if (/^\d+$/.test(handle)) {
        payload.user_id = Number(handle);
      } else {
        payload.username = handle;
      }
      await adminShopModerationActionData(payload);
      showToast(`Added @${handle} as ${newRole}.`, 'success');
      setUsername('');
      setNewRole('member');
      await load();
      onChanged?.();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not add member';
      setFormError(msg);
      showToast(msg, 'error');
    } finally {
      setAdding(false);
    }
  };

  const changeRole = async (member: AdminShopMember, role: MemberRole) => {
    if (member.role === role) return;
    setBusyId(member.id);
    try {
      await adminShopModerationAction({
        action: 'update_member',
        shop_id: shop.id,
        member_id: member.id,
        role,
      });
      showToast(`Updated ${member.username ? `@${member.username}` : `user #${member.user_id}`} to ${role}.`, 'success');
      await load();
      onChanged?.();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Could not update role', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setBusyId(removeTarget.id);
    try {
      await adminShopModerationAction({
        action: 'remove_member',
        shop_id: shop.id,
        member_id: removeTarget.id,
      });
      showToast(
        `Removed ${removeTarget.username ? `@${removeTarget.username}` : `user #${removeTarget.user_id}`}.`,
        'success'
      );
      setRemoveTarget(null);
      await load();
      onChanged?.();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Could not remove member', 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={adding || busyId !== null ? () => undefined : onClose}
        title="Shop members"
        description={`Who can accept contributor credits for ${shop.name}.`}
        size="lg"
        closeOnEscape={busyId === null && !adding}
        closeOnOverlayClick={busyId === null && !adding}
      >
        <div className="space-y-5">
          {loading ? (
            <p className="text-gray-400" role="status">
              Loading members…
            </p>
          ) : loadError ? (
            <div role="alert" className="alert-error">
              {loadError}
              <div className="mt-2">
                <Button type="button" variant="secondary" size="sm" onClick={() => void load()}>
                  Retry
                </Button>
              </div>
            </div>
          ) : members.length === 0 ? (
            <p className="text-sm text-amber-200/90">
              No members yet. Add at least one owner so the shop can accept contributor requests.
            </p>
          ) : (
            <ul className="space-y-2 list-none">
              {members.map((m) => {
                const isLastOwner = m.role === 'owner' && ownerCount <= 1;
                return (
                  <li
                    key={m.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/[0.08] bg-[#141414] px-3 py-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {m.username ? `@${m.username}` : `User #${m.user_id}`}
                      </p>
                      <p className="text-xs text-gray-500">id {m.user_id}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="sr-only" htmlFor={`member-role-${m.id}`}>
                        Role for {m.username || m.user_id}
                      </label>
                      <select
                        id={`member-role-${m.id}`}
                        className="input max-w-[140px] min-h-[40px] text-sm"
                        value={m.role}
                        disabled={busyId === m.id || isLastOwner}
                        onChange={(e) => void changeRole(m, e.target.value as MemberRole)}
                        title={isLastOwner ? 'Add another owner before demoting the last owner' : undefined}
                      >
                        <option value="owner">owner</option>
                        <option value="member">member</option>
                      </select>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={busyId === m.id || isLastOwner}
                        onClick={() => setRemoveTarget(m)}
                        title={isLastOwner ? 'Add another owner before removing the last owner' : 'Remove member'}
                      >
                        Remove
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <form onSubmit={addMember} className="border-t border-white/[0.06] pt-4 space-y-3">
            <h3 className="text-sm font-semibold text-white">Add member</h3>
            {formError ? (
              <div role="alert" className="alert-error">
                {formError}
              </div>
            ) : null}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
              <div>
                <label htmlFor="admin-shop-member-user" className="input-label">
                  Username or user id
                </label>
                <input
                  id="admin-shop-member-user"
                  type="text"
                  className="input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="@handle or numeric id"
                  disabled={adding}
                  autoComplete="off"
                />
              </div>
              <div>
                <label htmlFor="admin-shop-member-role" className="input-label">
                  Role
                </label>
                <select
                  id="admin-shop-member-role"
                  className="input min-h-[44px]"
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value as MemberRole)}
                  disabled={adding}
                >
                  <option value="member">member</option>
                  <option value="owner">owner</option>
                </select>
              </div>
              <Button type="submit" variant="primary" loading={adding} className="min-h-[44px]">
                Add
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Members can accept pending shop contributor credits. Keep at least one owner.
            </p>
          </form>

          <div className="flex justify-end">
            <Button type="button" variant="ghost" onClick={onClose} disabled={adding || busyId !== null}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(removeTarget)}
        title="Remove shop member?"
        description={
          removeTarget
            ? `Remove ${
                removeTarget.username ? `@${removeTarget.username}` : `user #${removeTarget.user_id}`
              } from ${shop.name}. They will no longer accept contributor requests for this shop.`
            : ''
        }
        confirmLabel="Remove"
        cancelLabel="Cancel"
        loading={busyId !== null}
        onCancel={() => setRemoveTarget(null)}
        onConfirm={() => void confirmRemove()}
      />
    </>
  );
};

export default AdminShopMembersModal;
